<template>
  <section
    :id="`stage-${stageNo}`"
    ref="rootEl"
    class="stage"
    :class="{ 'on-night': night, 'is-flipped': flipped, in: entered }"
    :style="colours"
    :aria-labelledby="titleId"
  >
    <div class="container">
      <div class="stage-top">
        <div class="stage-type">
          <div class="stage-mark">
            <ProjectMark :project="project" :size="night ? 92 : 60" />
          </div>

          <p class="stage-kicker label">
            {{ L(project.category)
            }}<span class="sep" aria-hidden="true">·</span>{{ project.year }}
          </p>

          <h2 :id="titleId" class="stage-title">
            <span>{{ titleHead }}</span
            ><em v-if="titleTail" class="stage-title-em">{{ titleTail }}</em>
          </h2>

          <p class="stage-say">{{ blurb }}</p>

          <ul v-if="stack.length" class="stage-stack">
            <li v-for="s in stack" :key="s">{{ s }}</li>
          </ul>

          <a
            v-if="liveUrl"
            class="stage-link"
            :href="liveUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{{ $t("stage.visit", { title: L(project.title) }) }}</span>
            <svg
              viewBox="0 0 24 24"
              width="15"
              height="15"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M7 17 17 7M8 7h9v9" />
            </svg>
          </a>
        </div>

        <StageCluster :assets="assets" :eager="index === 0" />
      </div>
    </div>
  </section>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import ProjectMark from "@/components/project/ProjectMark.vue";
import StageCluster from "@/components/project/StageCluster.vue";
import { prefersReducedMotion } from "@/composables/useDepth";
import { colourVars } from "@/composables/useProjectAssets";

const GALLERY_MAX = 8; // candidates; the cluster casts three of them by frame

export default {
  name: "StageSection",
  components: { ProjectMark, StageCluster },
  props: {
    project: { type: Object, required: true },
    index: { type: Number, default: 0 },
  },
  setup(props) {
    const { locale } = useI18n();
    const rootEl = ref(null);
    const entered = ref(prefersReducedMotion);
    let io = null;

    const stageNo = computed(() => props.project.stage || props.index + 1);
    // Every third stage takes the night ground, so the page keeps a dark beat
    // without every stage claiming one.
    const night = computed(() => stageNo.value % 3 === 0);
    const flipped = computed(() => stageNo.value % 2 === 0);
    const titleId = computed(() => `stage-title-${stageNo.value}`);

    const L = (f) =>
      typeof f === "string" ? f : (f && (f[locale.value] || f.en)) || "";

    /* The project's own image order is the authored one: the real product
       leads, marketing captures come after. An earlier version put `-web-`
       captures first and showed vetts.app instead of the vets app. */
    const gallery = computed(() =>
      (props.project.images || []).filter(Boolean).slice(0, GALLERY_MAX)
    );
    const assets = computed(() =>
      gallery.value.map((src) => ({ src, frame: frameFor(src), alt: altFor(src) }))
    );

    const frameFor = (src) => {
      const imgs = props.project.images || [];
      const meta = (props.project.imageMeta || [])[imgs.indexOf(src)];
      if (meta && meta.frame) return meta.frame;
      return String(src).includes("-web-") ? "window" : "bare";
    };

    /* Alt text is authored per shot. A shot without one is decorative: empty
       alt, no caption. The previous version fell back to the project summary,
       which printed the same sentence under every picture and again in the
       type column beside it. */
    const altFor = (src) => {
      const i = (props.project.images || []).indexOf(src);
      const alts = props.project.imageAlt || {};
      return (alts[locale.value] || alts.en || [])[i] || "";
    };

    const liveUrl = computed(() => {
      const u = props.project.liveUrl;
      return u && u !== "#" ? u : "";
    });

    const blurb = computed(
      () => L(props.project.summary) || L(props.project.description)
    );
    const stack = computed(() => (props.project.technologies || []).slice(0, 5));
    const colours = computed(() => colourVars(props.project));

    const words = computed(() => L(props.project.title).split(" "));
    const titleTail = computed(() =>
      words.value.length > 1 ? words.value[words.value.length - 1] : ""
    );
    const titleHead = computed(() =>
      words.value.length > 1
        ? words.value.slice(0, -1).join(" ") + " "
        : words.value.join(" ")
    );

    onMounted(() => {
      // One observer per stage, fired once: it flips `in`, which starts the
      // entrance on the cluster slots and the type column together.
      if (entered.value || typeof IntersectionObserver === "undefined") {
        entered.value = true;
        return;
      }
      io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          entered.value = true;
          io.disconnect();
          io = null;
        },
        { threshold: 0.22 }
      );
      io.observe(rootEl.value);
    });
    onBeforeUnmount(() => {
      if (io) io.disconnect();
    });

    return {
      rootEl,
      entered,
      stageNo,
      night,
      flipped,
      titleId,
      assets,
      blurb,
      stack,
      colours,
      liveUrl,
      titleHead,
      titleTail,
      L,
      frameFor,
      altFor,
    };
  },
};
</script>

<style scoped>
.stage {
  position: relative;
  padding: var(--s-10) 0;
  overflow: hidden;
}
.stage.on-night {
  background: var(--night);
  color: var(--on-night-2);
}

/* Colour channel 4: the project's own gradient as two discs, painted on the
   section rather than added as an element. */
.stage::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(
      44% 48% at 12% 18%,
      color-mix(in oklab, var(--p-from) 10%, transparent),
      transparent 70%
    ),
    radial-gradient(
      46% 44% at 88% 82%,
      color-mix(in oklab, var(--p-to) 10%, transparent),
      transparent 72%
    );
}
.stage.on-night::before {
  background-image: radial-gradient(
      44% 48% at 12% 18%,
      color-mix(in oklab, var(--p-from) 26%, transparent),
      transparent 70%
    ),
    radial-gradient(
      46% 44% at 88% 82%,
      color-mix(in oklab, var(--p-to) 26%, transparent),
      transparent 72%
    );
}

.stage-top {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 0.78fr) minmax(0, 1.22fr);
  gap: var(--s-9);
  align-items: center;
}
/* Alternate which side the artwork lands on, so four stages in a row do not
   read as one repeating template. */
.is-flipped .stage-top {
  grid-template-columns: minmax(0, 1.22fr) minmax(0, 0.78fr);
}
.is-flipped .stage-type {
  order: 2;
}

/* ── Type. k = 1.00, never transformed. ───────────────────── */
.stage-mark {
  margin-bottom: var(--s-5);
}
.stage-kicker {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  margin-bottom: var(--s-3);
}
.sep {
  margin: 0 0.7em;
  opacity: 0.5;
}
.stage-title {
  font-size: var(--t-h1);
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -0.028em;
  color: var(--ink);
  margin-bottom: var(--s-4);
  text-wrap: balance;
}
.on-night .stage-title {
  color: var(--on-night);
}
.stage-title-em {
  font-family: "Fraunces", Georgia, serif;
  font-style: italic;
  font-weight: 500;
  font-optical-sizing: auto;
  letter-spacing: -0.012em;
}
.stage-say {
  font-size: var(--t-lead);
  line-height: 1.5;
  max-width: 46ch;
  color: var(--text);
  text-wrap: pretty;
}
.on-night .stage-say {
  color: var(--on-night-2);
}

.stage-stack {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: var(--s-5) 0 0;
  padding: 0;
  list-style: none;
}
.stage-stack li {
  font-family: var(--font-measure);
  font-size: 11px;
  letter-spacing: 0.04em;
  color: var(--grey);
  padding: 4px 11px;
  border-radius: var(--r-pill);
  box-shadow: inset 0 0 0 var(--hair-w) var(--hairline);
}
.on-night .stage-stack li {
  color: var(--on-night-3);
  box-shadow: inset 0 0 0 var(--hair-w) rgba(244, 241, 236, 0.18);
}

.stage-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: var(--s-6);
  font-family: var(--font-measure);
  font-size: var(--m-label);
  font-weight: 500;
  letter-spacing: var(--ls-label);
  text-transform: uppercase;
  color: var(--accent);
}
.on-night .stage-link {
  color: var(--accent-light);
}
.stage-link svg {
  transition: transform var(--d-2) var(--ease-out);
}
.stage-link:hover svg {
  transform: translate(3px, -3px);
}

/* The type column arrives with the cluster: a short rise, opacity, and it
   is the only time this text ever moves. Once in, it is locked. */
.stage-type > * {
  opacity: 0;
  transform: translate3d(0, 14px, 0);
  transition: opacity var(--d-4) var(--ease-out), transform var(--d-4) var(--ease-out);
}
.stage-type > *:nth-child(1) { transition-delay: 0ms; }
.stage-type > *:nth-child(2) { transition-delay: 60ms; }
.stage-type > *:nth-child(3) { transition-delay: 120ms; }
.stage-type > *:nth-child(4) { transition-delay: 180ms; }
.stage-type > *:nth-child(5) { transition-delay: 240ms; }
.stage-type > *:nth-child(6) { transition-delay: 300ms; }
.in .stage-type > * {
  opacity: 1;
  transform: none;
}
@media (prefers-reduced-motion: reduce) {
  .stage-type > * {
    opacity: 1;
    transform: none;
    transition: none;
  }
}



@media (max-width: 1000px) {
  .stage-top,
  .is-flipped .stage-top {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--s-7);
  }
  .is-flipped .stage-type {
    order: 0;
  }
}

@media (max-width: 720px) {
  .stage {
    padding: var(--s-8) 0;
  }
}
</style>
